import React, { useState } from 'react';
import { BsArrowRepeat } from 'react-icons/bs';
import { useNavigate } from 'react-router-dom';
import Loader from '../../components/shared/Loader';
import { Button } from '../../components/ui/button';
import { ConsolePageHeader } from '../../components/shared/ListConsoleLayout';
import { useAsyncEffect } from '../../hooks/useAsyncEffect';
import { fetchOrderAnalytics } from '../transaction-monitoring/api/api';
import { EMPTY_ANALYTICS } from '../transaction-monitoring/constants/constants';
import type { OrderAnalytics } from '../transaction-monitoring/models/types';
import { useAuthStore } from '../../store/authStore';
import { isLightningMerchantDashboard } from '../../utils/merchantDashboardKey';
import TransactionDashboardCharts from './components/TransactionDashboardCharts';
import '../transaction-monitoring/TransactionMonitoring.css';

function formatDashboardAsOf() {
  return new Date().toLocaleString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const TransactionDashboard: React.FC = () => {
  const navigate = useNavigate();
  // Same derivation TransactionMonitoring uses for its own super-admin gating — adminType first,
  // falling back to role, because "role" becomes a custom roleCode once one is assigned.
  const authUser = useAuthStore((state) => state.user);
  const isLightningDashboard = isLightningMerchantDashboard(authUser);
  const isSuperAdminUser =
    String(authUser?.adminType ?? authUser?.role ?? '').trim().toUpperCase() === 'SUPER_ADMIN';
  const [analytics, setAnalytics] = useState<OrderAnalytics>(EMPTY_ANALYTICS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [asOf, setAsOf] = useState(formatDashboardAsOf);

  useAsyncEffect(async (isActive) => {
    setLoading(true);
    setError(null);
    try {
      const nextAnalytics = await fetchOrderAnalytics(0, 10000);
      if (!isActive()) return;
      setAnalytics(nextAnalytics);
      setAsOf(formatDashboardAsOf());
    } catch (err: unknown) {
      if (!isActive()) return;
      console.error('Failed to fetch dashboard data:', err);
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Failed to load transaction dashboard');
      setAnalytics(EMPTY_ANALYTICS);
    } finally {
      if (isActive()) setLoading(false);
    }
  }, [refreshKey]);

  const headerActions = (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="tm-console-action-btn tm-console-action-btn--outline gap-1.5"
        onClick={() => setRefreshKey((key) => key + 1)}
        disabled={loading}
      >
        <BsArrowRepeat className="h-3 w-3 shrink-0" aria-hidden />
        Refresh
      </Button>
      <Button
        type="button"
        size="sm"
        className="tm-console-action-btn gap-1.5"
        onClick={() => navigate('/dashboard/transaction-monitoring')}
      >
        View Monitoring
      </Button>
    </>
  );

  const charts = loading ? (
    <div className="tm-console-loading">
      <Loader size="lg" label="Loading dashboard..." />
    </div>
  ) : (
    <TransactionDashboardCharts analytics={analytics} isSuperAdminUser={isSuperAdminUser} />
  );

  return (
    <div className={isLightningDashboard ? 'tm-page sf-dashboard dh-page' : 'tm-page dh-page'}>
      <ConsolePageHeader
        title="Transaction Analytics"
        subtitle={`As of ${asOf}`}
        actions={headerActions}
      />
      {error && <div className="tm-error-banner">{error}</div>}
      {charts}
    </div>
  );
};

export default TransactionDashboard;
